import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { ExternalLink, Phone, Mail, MessageSquare, ClipboardCopy, FileCheck2, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openAircraftPeek, dealUrl, accountUrl } from '@/lib/links.js';
import { formatHumanDate } from '@/lib/format.js';
import { api } from '@/lib/api.js';
import { toast } from '@/components/ui/toaster.jsx';
import useMediaQuery from '@/hooks/useMediaQuery.js';
import { labelForRating } from '@/lib/ratings.js';
import PersonIdentityForm from '@/components/people/PersonIdentityForm.jsx';

// BNO Quote Desk entry point on the contact record (spec: "and on the contact
// record where a pilot is attached"). Lazy: heavy sheet.
const BnoQuoteDeskSheet = lazy(() => import('@/components/sheets/BnoQuoteDeskSheet.jsx'));

const PHF_VARIANT = {
  verified: 'success',
  submitted: 'info',
  in_progress: 'warning',
  invited: 'outline',
};

export default function ContactPeekSheet({ id, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const aborted = useRef(false);
  const openPeek = usePeekStore((s) => s.openPeek);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [copying, setCopying] = useState(false);
  const [bnoOpen, setBnoOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // Bumped after a person edit: the door swept this peek's cache, so it re-reads.
  const [reloadKey, setReloadKey] = useState(0);

  // "Copy PHF" — pull the pilot's canonical RTQ block (same renderer as the UW
  // email) and put it on the clipboard as rich HTML + plain text, so the broker
  // can paste it straight into a carrier email instead of re-typing it.
  async function copyPhf() {
    setCopying(true);
    try {
      const r = await api.get(`/api/tracon/contact/${encodeURIComponent(id)}/phf-block`);
      if (!r?.html) throw new Error('empty block');
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([r.html], { type: 'text/html' }),
            'text/plain': new Blob([r.text || ''], { type: 'text/plain' }),
          }),
        ]);
      } catch {
        // Older clipboard impls / non-HTTPS — fall back to plain text.
        await navigator.clipboard.writeText(r.text || '');
      }
      toast.success(`Copied ${r.name || 'PHF'} — paste into a carrier email`);
      // A name the block printed blank (ruling 4 at the flip), or a name that could not be checked.
      for (const w of (Array.isArray(r?.warnings) ? r.warnings : [])) toast.info(w);
    } catch {
      toast.error('Could not copy PHF block');
    } finally {
      setCopying(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    aborted.current = false;
    setLoading(true);
    setError(null);
    api
      .get(`/api/tracon/contact/${encodeURIComponent(id)}/peek`)
      .then((r) => {
        if (!aborted.current) setData(r);
      })
      .catch((err) => {
        if (!aborted.current) setError(err);
      })
      .finally(() => {
        if (!aborted.current) setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, [id, reloadKey]);

  // A different person closes the form.
  useEffect(() => { setEditing(false); }, [id]);

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className="flex flex-col overflow-hidden p-0 sm:max-w-lg"
      >
        <div className="p-6 pb-0">
          <SheetHeader>
            <div className="flex items-start justify-between gap-3">
              <SheetTitle>{data?.name || 'Contact'}</SheetTitle>
              {data && !editing ? (
                <Button type="button" variant="outline" size="xs" onClick={() => setEditing(true)} title="Edit this person's name, email and phone">
                  <Pencil size={12} strokeWidth={1.5} />
                  Edit
                </Button>
              ) : null}
            </div>
            <SheetDescription>
              {data?.certificate_type || 'Pilot'}
              {data?.total_flight_hours
                ? ` · ${data.total_flight_hours} hrs`
                : ''}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && !data ? (
            <p className="text-sm text-text-muted">Loading…</p>
          ) : error ? (
            <p className="text-sm text-accent">
              Failed to load contact: {error.message || 'unknown'}
            </p>
          ) : data ? (
            <>
              {/* The person edit form (people-create-door C4): the one read, the one door. */}
              {editing ? (
                <PersonIdentityForm contactId={id} onSaved={() => setReloadKey((k) => k + 1)} onCancel={() => setEditing(false)} />
              ) : null}

              {/* Contact actions */}
              <div className="flex flex-wrap gap-2">
                {data.phone ? (
                  <>
                    <a
                      href={`tel:${data.phone}`}
                      className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs text-text hover:bg-border/30"
                    >
                      <Phone size={12} strokeWidth={1.5} />
                      {data.phone}
                    </a>
                    <a
                      href={`sms:${data.phone}`}
                      className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs text-text hover:bg-border/30"
                    >
                      <MessageSquare size={12} strokeWidth={1.5} />
                      SMS
                    </a>
                  </>
                ) : null}
                {data.email ? (
                  <a
                    href={`mailto:${data.email}`}
                    className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs text-text hover:bg-border/30"
                  >
                    <Mail size={12} strokeWidth={1.5} />
                    {data.email}
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={copyPhf}
                  disabled={copying}
                  title="Copy this pilot's PHF block for a carrier email"
                  className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs text-text hover:bg-border/30 disabled:opacity-50"
                >
                  <ClipboardCopy size={12} strokeWidth={1.5} />
                  {copying ? 'Copying…' : 'Copy PHF'}
                </button>
                <button
                  type="button"
                  onClick={() => setBnoOpen(true)}
                  title="Quote a non-owned (renter/CFI) policy from this pilot's record"
                  className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs text-text hover:bg-border/30"
                >
                  <FileCheck2 size={12} strokeWidth={1.5} />
                  Quote non-owned
                </button>
              </div>

              {/* Ratings */}
              {data.ratings ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Ratings
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(data.ratings)
                      ? data.ratings
                      : [data.ratings]
                    ).map((r) => (
                      <Badge key={r} variant="outline" className="text-[10px]">
                        {labelForRating(r)}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Medical */}
              {data.medical_class || data.medical_date ? (
                <div className="flex gap-4 text-xs text-text-muted">
                  {data.medical_class ? (
                    <span>{data.medical_class} medical</span>
                  ) : null}
                  {data.medical_date ? (
                    <span>Issued {formatHumanDate(data.medical_date) || data.medical_date}</span>
                  ) : null}
                </div>
              ) : null}

              {/* PHF status */}
              {data.phf_status ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    PHF Status
                  </h4>
                  <Badge
                    variant={PHF_VARIANT[data.phf_status] || 'outline'}
                    className="text-[10px]"
                  >
                    {data.phf_status.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                  {data.phf_updated_at ? (
                    <span className="ml-2 text-[10px] text-text-dim">
                      {formatHumanDate(data.phf_updated_at)}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {/* Aircraft they fly */}
              {data.pilot_of?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Aircraft they fly
                  </h4>
                  <ul className="space-y-1">
                    {data.pilot_of.map((a) => (
                      <li key={a.aircraft_id}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPeek(openAircraftPeek(a.aircraft_id));
                          }}
                          className="text-sm font-mono font-medium text-text hover:underline"
                        >
                          {a.tail_number || a.aircraft_id}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Accounts (account-people-door T27, P1, D-31): the server sends this block at
                  account_people "both" only; each account with the server's role words and
                  "Main account" beside it. A failed read renders the server's words, never
                  "no accounts". The deals list below is unchanged. */}
              {data.accounts ? (
                <div data-peek-accounts={data.accounts.state}>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    Accounts
                  </h4>
                  {data.accounts.state !== 'known' ? (
                    <p className="text-xs text-warning">{data.accounts.words || 'Could not read this person\'s accounts in TRACON.'}</p>
                  ) : (data.accounts.rows || []).length === 0 ? (
                    <p className="text-xs text-text-dim">This person is not linked to any account in TRACON.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {data.accounts.rows.map((a) => (
                        <li key={a.account_id || a.crm_account_id} className="flex flex-wrap items-baseline gap-1.5" data-peek-account={a.crm_account_id || a.account_id}>
                          {a.crm_account_id ? (
                            <a href={accountUrl(a.crm_account_id)} className="text-sm font-medium text-text hover:underline">{a.name || a.crm_account_id}</a>
                          ) : (
                            <span className="text-sm font-medium text-text">{a.name || 'account name not recorded'}</span>
                          )}
                          {a.main_word ? <span className="rounded-pill border border-accent px-1.5 text-[10px] text-text" data-peek-main>{a.main_word}</span> : null}
                          {(a.role_words || []).map((w, i) => (
                            <span key={`${w}${i}`} className="rounded-pill border border-border px-1.5 text-[10px] text-text-muted" data-peek-role>{w}</span>
                          ))}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}

              {/* On policies */}
              {data.on_deals?.length > 0 ? (
                <div>
                  <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-dim">
                    On policies
                  </h4>
                  <ul className="space-y-1.5">
                    {data.on_deals.map((d) => {
                      const contactName = (data.name || '').toLowerCase().trim();
                      const acctName = (d.account_name || '').toLowerCase().trim();
                      const showAcct = acctName && contactName && !acctName.includes(contactName) && !contactName.includes(acctName);
                      return (
                        <li key={d.deal_id}>
                          <a
                            href={dealUrl(d.account_id || d.deal_id, d.deal_id)}
                            className="flex items-baseline gap-2 text-text hover:underline"
                          >
                            <span className="text-sm font-medium">
                              {d.term_year ? `${d.term_year} term` : (d.app_number || 'Policy')}
                            </span>
                            {d.stage ? (
                              <Badge variant="outline" className="text-[9px]">
                                {d.stage}
                              </Badge>
                            ) : null}
                            <Badge
                              // `outline`, NOT `muted`. `muted` carries a
                              // line-through, and an APPROVED pilot is not a
                              // revoked one. The badge was printing the live
                              // half of a two-state pair as if it had been
                              // struck off the policy.
                              variant={d.role === 'primary' ? 'accent' : 'outline'}
                              className="text-[9px]"
                            >
                              {d.role === 'primary' ? 'PRIMARY' : 'APPROVED'}
                            </Badge>
                          </a>
                          {showAcct ? (
                            <div className="mt-0.5 text-[10px] text-text-dim">{d.account_name}</div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        {/* Footer: CRM is for viewing; a person's name, email and phone are
            edited in TRACON only (people-create-door C4, ruling 1). */}
        {data?.crm_url ? (
          <div className="border-t border-border bg-surface px-6 py-3 flex items-center justify-between gap-3">
            <span className="text-[11px] text-text-dim">Edit name, email and phone here, not in CRM.</span>
            <a
              href={data.crm_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-1.5 text-xs text-text-muted hover:text-text"
            >
              <ExternalLink size={12} strokeWidth={1.5} />
              View in CRM
            </a>
          </div>
        ) : null}
      </SheetContent>

      {bnoOpen ? (
        <Suspense fallback={null}>
          <BnoQuoteDeskSheet
            contactId={id}
            pilotName={data?.name || ''}
            onClose={() => setBnoOpen(false)}
          />
        </Suspense>
      ) : null}
    </Sheet>
  );
}
